/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PbCcElementDto } from '@gh/builder-shared-data';
import { getRouteInitData, queryFormEditable, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { SelectionModel, emptySelectionModel, selectionModelAfterRowRemoved } from '@gh/core-data';
import { FocusedCell } from '@gh/core-ui-grid';
import { DialogService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { filter$, map$, switchMap$, take$, takeUntil$ } from '@gh/rx/operators';
import { Observable, Subject, Subscription } from 'rxjs';
import { getPbCcStructureId, PbCcStructure } from '../../../resources/price-book.model';
import { PbCommandService } from '../pb-command.service';
import {
  PbDetailsState,
  PbDetailsStateEventType,
  queryCostCenterStructures,
  querySelectedCostCenterId,
  querySelectedStructureId,
  queryStructureFocusedCell,
} from '../pb-details.state';
import { PbDetailsStructureGridDefinitionService } from './pb-details-structure-grid-definition.service';

@Component({
  standalone: false,
  selector: 'gh-pb-details-structure-list',
  templateUrl: 'pb-details-structure-list.component.html',
  styleUrls: ['pb-details-structure-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PbDetailsStructureGridDefinitionService],
})
export class PbDetailsStructureListComponent implements OnInit, OnDestroy {
  selectedStructureId$: Observable<Maybe<ID>>;
  structures$: Observable<PbCcElementDto[]>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  editable: boolean = true;
  sourceSelectionModel = emptySelectionModel();
  private lastSelectedCostCenterId: Maybe<ID>;
  private costCenterIdSubscription: Subscription;
  private readonly destroy$ = new Subject<void>();

  constructor(private route: ActivatedRoute,
              private state: State<PbDetailsState>,
              private commands: PbCommandService,
              private dialogService: DialogService,
              public gridDefinition: PbDetailsStructureGridDefinitionService) {

  }

  ngOnInit(): void {
    const { priceBookItemsComponentsGridLayout } = getRouteInitData(this.route);

    this.gridDefinition.bind(priceBookItemsComponentsGridLayout);

    const costCenterId$ = this.state.select(querySelectedCostCenterId).pipe(filter$(Boolean));

    this.structures$ = costCenterId$.pipe(
      switchMap$((costCenterId: any) => this.state.select((state) => queryCostCenterStructures(state, costCenterId))));
    this.selectedStructureId$ = this.state.select(querySelectedStructureId);
    this.focusedCell$ = this.state.select(queryStructureFocusedCell);
    this.editable = queryFormEditable(this.state.value);

    this.costCenterIdSubscription = costCenterId$.subscribe((id: any) => {
      if (this.lastSelectedCostCenterId !== void 0 && this.lastSelectedCostCenterId !== id) {
        this.onSourceSelectionChanged(emptySelectionModel());
      }
      this.lastSelectedCostCenterId = id;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.costCenterIdSubscription.unsubscribe();
  }

  onAdd(): void {
    this.commands.structureAdd();
  }

  onDelete(structure: PbCcStructure): void {
    const id = getPbCcStructureId(structure);
    this.commands.structureRemove(structure).pipe(
      takeUntil$(this.destroy$),
    ).subscribe(() => {
      const costCenterId = this.state.project(querySelectedCostCenterId);
      const structures = costCenterId
        ? this.state.project((s) => queryCostCenterStructures(s, costCenterId)) || []
        : [];
      const next = selectionModelAfterRowRemoved(
        this.sourceSelectionModel,
        id,
        structures.length === 0,
      );
      if (next !== this.sourceSelectionModel) {
        this.onSourceSelectionChanged(next);
      }
    });
  }

  onSelectedStructureChanged(id?: ID): void {
    this.commands.structureSelect(id);
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  onSourceSelectionChanged(model: SelectionModel): void {
    this.sourceSelectionModel = model;
  }

  getSelectedStructures(): Observable<PbCcElementDto[]> {
    return this.structures$.pipe(
      map$((structures) => {
        if (!structures || structures.length === 0) {
          return [];
        }

        if (this.sourceSelectionModel.isExcludeMode) {
          return structures.filter((structure) => {
            const id = getPbCcStructureId(structure);
            return !this.sourceSelectionModel.idList.includes(id);
          });
        } else {
          return structures.filter((structure) => {
            const id = getPbCcStructureId(structure);
            return this.sourceSelectionModel.idList.includes(id);
          });
        }
      })
    );
  }

  hasSelectedItems(): Observable<boolean> {
    return this.getSelectedStructures().pipe(
      map$((selectedStructures) => selectedStructures.length > 0)
    );
  }

  onDeleteSelected(): void {
    this.getSelectedStructures().pipe(
      take$(1),
      filter$((selectedStructures) => selectedStructures.length > 0),
      switchMap$((selectedStructures) => {
        const count = selectedStructures.length;
        const confirmConfig = count === 1
          ? yesNoConfirmation('msg_bulk_remove_single_item', { code: selectedStructures[0].code })
          : yesNoConfirmation('msg_bulk_remove_n_items', { count: String(count) });
        return this.dialogService.confirmation(confirmConfig).pipe(
          filter$(yesAnswer),
          map$(() => selectedStructures));
      }),
    ).subscribe((selectedStructures) => {
      selectedStructures.forEach((structure) => {
        this.state.mutate({
          type: PbDetailsStateEventType.StructureRemove,
          structureId: getPbCcStructureId(structure),
        });
      });
      this.sourceSelectionModel = emptySelectionModel();
      this.onSourceSelectionChanged(this.sourceSelectionModel);
      setTimeout(() => {
        this.onFocusedCellChanged();
      }, 0);
    });
  }
}
